/**
 * Área de gráfico diário v2 (extraída de GoogleAdsV2/components/DailyChart/ChartFrame.tsx; mockup: bloco de 230px): eixo esquerdo (e direito nos modelos de 2 eixos), grade tracejada,
 * SVG 1000×200 esticado (áreas e linhas com stroke sem escala), rótulos do eixo x e hover por coluna (guia, pontos com
 * halo, tooltip "ter, 29/09/2026" + linhas coloridas).
 *
 * Adaptado de `DashboardV2/components/charts/kit.tsx` `Frame`. Acessível por teclado: a área recebe foco; ←/→ andam
 * pelos dias, Home/End vão às pontas, Esc fecha o tooltip; o conteúdo do tooltip é anunciado (aria-live).
 */
import { useState, type KeyboardEvent } from 'react';
import { v } from '../theme';
import { colX, tipDate, VH, VW, xLabels, type Tick } from './geometry';
import './chart.css';

export interface FrameLine { d: string; s: string; w: number; op?: number; da?: string }
export interface FrameArea { d: string; f: string; o: number }
export interface DotSpec { x: number; y: number; c: string }
export interface FrameTipRow { l: string; v: string; c: string; fg: string }

/** O que o quadro desenha (o modelo de cada tela monta isto). */
export interface FrameSpec {
    dates: string[];
    /** Eixo esquerdo e (modelos de 2 eixos) direito. */
    yt: Tick[];
    ytr: Tick[];
    areas: FrameArea[];
    lines: FrameLine[];
    /** Pontos com halo no dia em hover. */
    dots: (i: number) => DotSpec[];
    tip: (i: number) => FrameTipRow[];
    /** 'full' = guia clara de altura toda no hover, sem pontos (streamgraph). */
    guide: 'soft' | 'full';
}

export const AREA_H = 230;
const AXIS_W = 60;
const RPAD_DUAL = 54;
const RPAD = 8;

const SR_ONLY = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 } as const;

const Dot = ({ d }: { d: DotSpec }) => (
    <div style={{ position: 'absolute', left: `${d.x / 10}%`, top: `${(d.y / VH) * 100}%`, width: 9, height: 9, margin: '-4.5px 0 0 -4.5px', borderRadius: '50%', background: d.c, boxShadow: `0 0 0 3px ${v('surface')}`, pointerEvents: 'none' }} />
);

export function ChartFrame({ spec, label, height = AREA_H, tipTitle = tipDate }: { spec: FrameSpec; label: string; height?: number; tipTitle?: (iso: string) => string }) {
    const [hv, setHv] = useState<number | null>(null);
    const [kbd, setKbd] = useState(false);
    const { dates, yt, ytr } = spec;
    const n = dates.length;
    const hi = hv != null && hv < n ? hv : null;
    const rpad = ytr.length ? RPAD_DUAL : RPAD;
    const hx = hi != null ? colX(hi, n) : 0;
    const rows = hi != null ? spec.tip(hi) : [];
    const axis = { position: 'absolute' as const, fontSize: 11, color: v('dim'), fontVariantNumeric: 'tabular-nums' as const, transform: 'translateY(-50%)', whiteSpace: 'nowrap' as const };

    const onKey = (e: KeyboardEvent) => {
        if (!n) return;
        const cur = hi ?? -1;
        let next: number | null | undefined;
        if (e.key === 'ArrowRight') next = Math.min(n - 1, cur + 1);
        else if (e.key === 'ArrowLeft') next = cur < 0 ? n - 1 : Math.max(0, cur - 1);
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = n - 1;
        else if (e.key === 'Escape') next = null;
        if (next === undefined) return;
        e.preventDefault();
        setKbd(true);
        setHv(next);
    };

    return (
        <div style={{ position: 'relative', height }}>
            {yt.map((t, i) => <span key={i} aria-hidden style={{ ...axis, left: 0, width: AXIS_W - 10, textAlign: 'right', top: `${t.top}%` }}>{t.l}</span>)}
            {ytr.map((t, i) => <span key={i} aria-hidden style={{ ...axis, right: 0, width: RPAD_DUAL - 8, textAlign: 'left', top: `${t.top}%` }}>{t.l}</span>)}
            <div className="gads-dc-plot" tabIndex={0} role="group"
                aria-label={`${label}. Use as setas para ver cada dia.`}
                onKeyDown={onKey}
                onBlur={() => { if (kbd) { setHv(null); setKbd(false); } }}
                style={{ position: 'absolute', left: AXIS_W, right: rpad, top: 0, bottom: 24 }}>
                {yt.map((t, i) => <div key={i} style={{ position: 'absolute', left: 0, right: 0, top: `${t.top}%`, borderTop: `1px dashed ${v('border-soft')}` }} />)}
                <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="none" aria-hidden
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                    {spec.areas.map((a, i) => <path key={i} d={a.d} fill={a.f} fillOpacity={a.o} />)}
                    {spec.lines.map((l, i) => (
                        <path key={i} d={l.d} fill="none" stroke={l.s} strokeWidth={l.w} strokeOpacity={l.op ?? 1} strokeDasharray={l.da}
                            vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
                    ))}
                    {hi != null && spec.guide === 'full' && (
                        <path d={`M${hx},0 L${hx},${VH}`} stroke={v('text')} strokeOpacity={0.6} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                    )}
                </svg>
                {hi != null && (
                    <>
                        {spec.guide === 'soft' && <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${hx / 10}%`, borderLeft: `1px solid ${v('border-3')}`, pointerEvents: 'none' }} />}
                        {spec.guide === 'soft' && spec.dots(hi).map((d, i) => <Dot key={i} d={d} />)}
                        <div aria-hidden style={{ position: 'absolute', top: 4, left: `${hx / 10}%`, transform: hx > 650 ? 'translateX(calc(-100% - 14px))' : 'translateX(14px)', background: v('popover'), border: `1px solid ${v('border-3')}`, borderRadius: 10, padding: '10px 12px', minWidth: 190, boxShadow: 'var(--rv-shadow)', pointerEvents: 'none', zIndex: 3, display: 'flex', flexDirection: 'column', gap: 5, whiteSpace: 'nowrap' }}>
                            <span style={{ fontSize: 12, color: v('muted') }}>{tipTitle(dates[hi])}</span>
                            {rows.map((r, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: 2, background: r.c, flexShrink: 0 }} />
                                    <span style={{ color: v('text-3'), flex: 1 }}>{r.l}</span>
                                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: r.fg }}>{r.v}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <div onMouseLeave={() => { setHv(null); setKbd(false); }} style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                    {dates.map((d, i) => <div key={d} onMouseEnter={() => { setKbd(false); setHv(i); }} style={{ flex: 1, height: '100%' }} />)}
                </div>
                <div aria-live="polite" style={SR_ONLY}>
                    {kbd && hi != null ? `${tipTitle(dates[hi])}: ${rows.map((r) => `${r.l} ${r.v}`).join('; ')}` : ''}
                </div>
            </div>
            <div aria-hidden style={{ position: 'absolute', left: AXIS_W, right: rpad, bottom: 0, height: 16 }}>
                {xLabels(dates).map((x) => (
                    <span key={x.i} style={{ position: 'absolute', left: `${x.l}%`, transform: 'translateX(-50%)', fontSize: 11, color: v('dim'), whiteSpace: 'nowrap' }}>{x.t}</span>
                ))}
            </div>
        </div>
    );
}
